import { cloneDeep } from 'lodash'

/** Agent-html elastic section ids: /^[a-z][a-z0-9-_]+$/ */
export const AGENT_SECTION_SLUG_REGEXP = /^[a-z][a-z0-9-_]+$/

const AGENT_I18N_FIELD_ID_REGEXP = /^[a-z0-9][a-z0-9-]{0,120}$/

/**
 * Lowercase slug for agent-html section ids; coerce invalid characters to hyphens.
 * @param {string} raw
 * @returns {string}
 */
export function normalizeAgentSectionSlug(raw) {
	let s = String(raw || '').trim().toLowerCase()
	if (!s) return ''
	s = s.replace(/\./g, '-')
	s = s.replace(/[^a-z0-9-_]/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '')
	if (!s) return ''
	if (!/^[a-z]/.test(s)) {
		s = `section-${s}`
	}
	return s
}

/**
 * Resolve a section id against existing agent-html ids (case-insensitive legacy match).
 * @param {string} raw
 * @param {string[]} existingAgentIds
 * @returns {string}
 */
export function resolveAgentSectionSlug(raw, existingAgentIds = []) {
	const normalized = normalizeAgentSectionSlug(raw)
	if (!normalized) return ''
	const existing = Array.isArray(existingAgentIds) ? existingAgentIds.map(id => String(id)) : []
	if (existing.includes(normalized)) {
		return normalized
	}
	const lower = normalized.toLowerCase()
	for (let i = 0; i < existing.length; i += 1) {
		if (String(existing[i]).toLowerCase() === lower) {
			return String(existing[i])
		}
	}
	return normalized
}

/**
 * @param {string} raw
 * @returns {string}
 */
export function normalizeAgentI18nFieldId(raw) {
	return String(raw || '').trim().toLowerCase()
}

/**
 * @param {*} raw
 * @returns {object|null} { fieldId: { locale: string } }
 */
export function normalizeAgentI18nObject(raw) {
	if (raw == null || raw === '') {
		return null
	}
	const obj = typeof raw === 'string' ? (() => {
		try {
			return JSON.parse(raw)
		} catch (_e) {
			return null
		}
	})() : raw
	if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
		return null
	}
	const out = {}
	Object.keys(obj).forEach((fieldId) => {
		const id = normalizeAgentI18nFieldId(fieldId)
		if (!AGENT_I18N_FIELD_ID_REGEXP.test(id)) {
			return
		}
		const perLocale = obj[fieldId]
		if (!perLocale || typeof perLocale !== 'object' || Array.isArray(perLocale)) {
			return
		}
		out[id] = {}
		Object.keys(perLocale).forEach((loc) => {
			const v = perLocale[loc]
			if (v == null) {
				return
			}
			out[id][String(loc).trim()] = String(v)
		})
		if (!Object.keys(out[id]).length) {
			delete out[id]
		}
	})
	return Object.keys(out).length ? out : null
}

/**
 * Deep-merge agent section i18n maps: fieldId -> { locale -> text }.
 * Incoming overrides per locale; preserves existing fields/locales not mentioned.
 * @param {object|null} existing
 * @param {object|null} incoming normalized partial
 * @returns {object}
 */
export function mergeAgentI18nObjects(existing, incoming) {
	const base = existing && typeof existing === 'object' && !Array.isArray(existing)
		? cloneDeep(existing)
		: {}
	if (!incoming || typeof incoming !== 'object' || Array.isArray(incoming)) {
		return base
	}
	Object.keys(incoming).forEach((fieldId) => {
		const id = normalizeAgentI18nFieldId(fieldId)
		if (!AGENT_I18N_FIELD_ID_REGEXP.test(id)) {
			return
		}
		const perLocale = incoming[fieldId]
		if (!perLocale || typeof perLocale !== 'object' || Array.isArray(perLocale)) {
			return
		}
		if (!base[id]) {
			base[id] = {}
		}
		Object.keys(perLocale).forEach((loc) => {
			const v = perLocale[loc]
			if (v === undefined) {
				return
			}
			base[id][String(loc).trim()] = String(v)
		})
	})
	return base
}

/**
 * Read authoritative agent copy from page.strings (`texts.agent-<sectionId>-<fieldId>`).
 * @param {object|null|undefined} pageStrings
 * @param {string[]} locales
 * @param {string} sectionId
 * @returns {object|null}
 */
export function collectAgentSectionI18nFromPageStrings(pageStrings, locales, sectionId) {
	const sid = String(sectionId || '').trim()
	if (!sid || !pageStrings || typeof pageStrings !== 'object') {
		return null
	}
	const prefixRe = new RegExp(`^agent-${sid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-`, 'i')
	const localeList = Array.isArray(locales) && locales.length
		? locales.map(l => String(l))
		: Object.keys(pageStrings)
	const out = {}
	for (let li = 0; li < localeList.length; li += 1) {
		const locale = localeList[li]
		const texts = pageStrings[locale] && pageStrings[locale].texts
		if (!texts || typeof texts !== 'object') {
			// eslint-disable-next-line no-continue
			continue
		}
		Object.keys(texts).forEach((slug) => {
			const m = slug.match(prefixRe)
			if (!m) {
				return
			}
			const fieldId = normalizeAgentI18nFieldId(slug.slice(m[0].length))
			if (!fieldId || !AGENT_I18N_FIELD_ID_REGEXP.test(fieldId)) {
				return
			}
			if (!out[fieldId]) {
				out[fieldId] = {}
			}
			out[fieldId][locale] = texts[slug] != null ? String(texts[slug]) : ''
		})
	}
	return Object.keys(out).length ? out : null
}

/**
 * CTA / tile links resolve href from `texts.agent-<section>-<base>-href`; strip hardcoded hrefs.
 * @param {string} html
 * @returns {string}
 */
export function normalizeAgentHtmlHrefPlaceholders(html) {
	const s = String(html || '')
	if (!s) return s
	return s.replace(
		/<a\b(?=[^>]*\bdata-i18n-(?:cta|href-field)\b)[^>]*>/gi,
		(tag) => {
			if (/\bhref\s*=/i.test(tag)) {
				return tag.replace(/\bhref\s*=\s*(['"])[^'"]*\1/i, 'href="#"')
			}
			return tag.replace(/>$/, ' href="#">')
		},
	)
}

/**
 * @param {object} row
 * @returns {object}
 */
export function normalizeAgentSyncSectionRow(row) {
	if (!row || typeof row !== 'object' || Array.isArray(row)) {
		return row
	}
	const out = { ...row }
	if (out.id != null) {
		out.id = normalizeAgentSectionSlug(out.id)
	}
	if (out.afterSectionId != null && String(out.afterSectionId).trim() !== '') {
		out.afterSectionId = normalizeAgentSectionSlug(out.afterSectionId)
	}
	if (Object.prototype.hasOwnProperty.call(out, 'html') && out.html != null) {
		out.html = normalizeAgentHtmlHrefPlaceholders(String(out.html))
	}
	if (Object.prototype.hasOwnProperty.call(out, 'i18n')) {
		if (out.i18n === null) {
			// explicit clear
		} else {
			const normalized = normalizeAgentI18nObject(out.i18n)
			if (normalized) {
				out.i18n = normalized
			}
		}
	}
	return out
}
